import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { patchMe } from "../../features/auth/api/auth";
import { authQueryKeys } from "../../features/auth/api/query-keys";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./AuthProvider";
import { applyTheme, normalizeTheme, readStoredTheme, THEME_DARK, THEME_LIGHT } from "./theme";
import {
  NUMERAL_EN,
  NUMERAL_FA,
  getNumeralSystem,
  setNumeralSystem as setStoreNumeralSystem,
} from "../../shared/utils/numberLocale";

const PreferencesContext = createContext(null);

function readCustomization(profile) {
  const raw =
    profile?.customization && typeof profile.customization === "object"
      ? profile.customization
      : {};
  return {
    theme: normalizeTheme(raw.theme || readStoredTheme()),
    numeralSystem: raw.numeralSystem === NUMERAL_EN ? NUMERAL_EN : NUMERAL_FA,
    sidebarOpen: raw.sidebarOpen !== false,
  };
}

export function PreferencesProvider({ children }) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const hydratedIdRef = useRef(null);
  const [theme, setThemeState] = useState(() => readStoredTheme());
  const [sidebarOpen, setSidebarOpenState] = useState(true);
  const [saving, setSaving] = useState(false);

  const persist = useCallback(
    async (patch) => {
      if (!profile) {
        if (patch.theme) applyTheme(patch.theme);
        if (patch.numeralSystem) setStoreNumeralSystem(patch.numeralSystem);
        return;
      }
      const next = {
        ...(profile.customization && typeof profile.customization === "object"
          ? profile.customization
          : {}),
        ...patch,
      };
      queryClient.setQueryData(authQueryKeys.me(), (prev) => {
        const prevProfile = prev?.profile || profile;
        return {
          ...(prev || {}),
          profile: { ...prevProfile, customization: next },
        };
      });
      setSaving(true);
      try {
        const data = await patchMe({ customization: patch });
        if (data?.profile) {
          queryClient.setQueryData(authQueryKeys.me(), data);
        }
      } catch (err) {
        console.warn("Could not persist profile.customization", err);
      } finally {
        setSaving(false);
      }
    },
    [profile, queryClient],
  );

  useEffect(() => {
    const id = profile?.personnel_id ?? (profile ? "session" : null);
    if (!id || hydratedIdRef.current === id) return;
    hydratedIdRef.current = id;
    const prefs = readCustomization(profile);
    setThemeState(applyTheme(prefs.theme));
    setStoreNumeralSystem(prefs.numeralSystem);
    setSidebarOpenState(prefs.sidebarOpen);
  }, [profile]);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback(
    (next) => {
      const value = applyTheme(next);
      setThemeState(value);
      persist({ theme: value });
    },
    [persist],
  );

  const setNumeralSystem = useCallback(
    (next) => {
      const value = next === NUMERAL_EN ? NUMERAL_EN : NUMERAL_FA;
      setStoreNumeralSystem(value);
      persist({ numeralSystem: value });
    },
    [persist],
  );

  const setSidebarOpen = useCallback(
    (next) => {
      const value = typeof next === "function" ? next(sidebarOpen) : !!next;
      setSidebarOpenState(value);
      persist({ sidebarOpen: value });
    },
    [persist, sidebarOpen],
  );

  const value = useMemo(
    () => ({
      theme,
      isDark: theme === THEME_DARK,
      setTheme,
      toggleTheme: () => setTheme(theme === THEME_DARK ? THEME_LIGHT : THEME_DARK),
      numeralSystem: getNumeralSystem(),
      setNumeralSystem,
      sidebarOpen,
      setSidebarOpen,
      saving,
    }),
    [theme, setTheme, setNumeralSystem, sidebarOpen, setSidebarOpen, saving],
  );

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences() {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error("usePreferences must be used within PreferencesProvider");
  }
  return context;
}

export { THEME_DARK, THEME_LIGHT };
